import { type Member, WARN_MEDIA_BYTES } from "@rhema/client";
import { Avatar, Button, cn, focusRing, IconButton, PlayIcon, Sheet, XIcon } from "@rhema/ui";
import {
  type KeyboardEvent,
  type PointerEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { toneFor } from "../data/view";
import { isHeic } from "../media/formats";
import { extensionOf, formatBytes, formatDuration } from "../media/prepare";
import type { Preparer } from "../media/preparer";
import { countLabel, itemLabel, moveItem, type Prepared, type TrayItem } from "../media/tray";

// The send tray: everything picked, before anything is encrypted or uploaded. Items can be
// reordered by dragging or with the keyboard, removed, and each given its own caption. Photos and
// videos go out together as one album. Pasting or dropping more while it is open adds to it.

const DRAG_PX = 6;

/** A tile's picture: the photo itself, or once ready, the converted preview of a HEIC photo or a video. */
function useTileImage(item: TrayItem, preparer: Preparer, original: boolean) {
  const [src, setSrc] = useState<string | null>(null);
  const [prepared, setPrepared] = useState<Prepared | null>(null);
  useEffect(() => {
    let live = true;
    let objectUrl: string | null = null;
    setPrepared(null);
    // Browsers cannot draw HEIC, so those wait for the converted preview, as videos do.
    const drawable =
      item.kind === "photo"
        ? isHeic(item.file).then(
            (h) => !h,
            () => false,
          )
        : Promise.resolve(false);
    void drawable.then((ok) => {
      if (!live || !ok) return;
      objectUrl = URL.createObjectURL(item.file);
      setSrc(objectUrl);
    });
    void Promise.all([preparer.get(item, original), drawable]).then(([p, ok]) => {
      if (!live) return;
      setPrepared(p);
      if (!ok && p.meta.thumbnail) setSrc(p.meta.thumbnail);
    });
    return () => {
      live = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item, preparer, original]);
  return { src, prepared };
}

function Tile({
  item,
  index,
  count,
  selected,
  dragging,
  preparer,
  original,
  onSelect,
  onRemove,
  onKey,
  onPointerDown,
  register,
}: {
  item: TrayItem;
  index: number;
  count: number;
  selected: boolean;
  dragging: boolean;
  preparer: Preparer;
  original: boolean;
  onSelect: () => void;
  onRemove: () => void;
  onKey: (e: KeyboardEvent<HTMLButtonElement>) => void;
  onPointerDown: (e: PointerEvent<HTMLButtonElement>) => void;
  register: (el: HTMLButtonElement | null) => void;
}) {
  const { src, prepared } = useTileImage(item, preparer, original);
  const label = itemLabel(item, index);
  const duration = item.kind === "video" ? prepared?.meta.duration : undefined;
  const hint = useId();
  return (
    <li
      data-tray-index={index}
      className={cn(
        "relative aspect-square overflow-hidden rounded-[10px] bg-surface-header transition-transform",
        dragging && "z-10 scale-[1.04] shadow-lift",
        selected && "outline-2 outline-offset-2 outline-blue",
      )}
    >
      <button
        ref={register}
        type="button"
        aria-label={`${item.kind === "file" ? `${label}, ${index + 1}` : `${label}`} of ${count}${item.kind === "file" || !item.file.name ? "" : `, ${item.file.name}`}${item.caption ? `, caption: ${item.caption}` : ""}`}
        aria-pressed={selected}
        aria-describedby={hint}
        onClick={onSelect}
        onKeyDown={onKey}
        onPointerDown={onPointerDown}
        className={cn(
          "absolute inset-0 block cursor-grab touch-none overflow-hidden rounded-[10px] border-0 bg-transparent p-0",
          focusRing,
          "focus-visible:outline-offset-[-3px]",
        )}
      >
        {src && item.kind !== "file" ? (
          <img src={src} alt="" draggable={false} className="h-full w-full object-cover" />
        ) : item.kind === "file" ? (
          <span className="flex h-full w-full flex-col items-center justify-center gap-1 bg-blue-tint px-1.5 text-center">
            <span className="text-[12px] font-bold text-blue">
              {extensionOf(item.file.name, item.file.type)}
            </span>
            <span className="line-clamp-2 text-[10.5px] leading-tight text-ink-secondary [overflow-wrap:anywhere]">
              {item.file.name}
            </span>
          </span>
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[11px] text-ink-meta">
            Preparing
          </span>
        )}
        {item.kind === "video" && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-blue text-white">
              <PlayIcon size={12} />
            </span>
          </span>
        )}
        {item.kind !== "file" && (
          <span className="absolute bottom-1.5 left-1.5 rounded-md bg-linen/95 px-1.5 py-px text-[11px] font-semibold text-ink">
            {duration !== undefined ? formatDuration(duration) : label}
          </span>
        )}
        <span id={hint} className="sr-only">
          Press Alt or Option with an arrow key to move it, and Delete to remove it.
        </span>
      </button>
      <IconButton
        label={`Remove ${label}`}
        variant="linen"
        size={26}
        onClick={onRemove}
        className="absolute top-1 right-1"
      >
        <XIcon size={12} strokeWidth={2.6} />
      </IconButton>
    </li>
  );
}

const MENTION = /(?:^|\s)@([^\s@]*)$/;

/** A caption box with the composer's Enter rule: Enter sends, Shift and Enter starts a new line. */
function CaptionField({
  label,
  value,
  members,
  onChange,
  onMention,
  onSubmit,
}: {
  label: string;
  value: string;
  /** People who can be @mentioned, in a group. */
  members: Member[];
  onChange: (value: string) => void;
  onMention: (name: string, userId: string) => void;
  onSubmit: () => void;
}) {
  const id = useId();
  const field = useRef<HTMLTextAreaElement>(null);
  const query = members.length ? MENTION.exec(value)?.[1] : undefined;
  const options =
    query === undefined
      ? []
      : members.filter((m) => m.user.display_name.toLowerCase().includes(query.toLowerCase())).slice(0, 5);
  // Grows with the text, up to five lines.
  // biome-ignore lint/correctness/useExhaustiveDependencies: measured again when the text changes
  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [value]);
  return (
    <div className="mt-4 flex flex-col gap-1">
      <label htmlFor={id} className="text-[12.5px] text-ink-secondary">
        {label}
      </label>
      {options.length > 0 && (
        <ul
          aria-label="People to mention"
          className="m-0 flex list-none flex-col gap-0.5 rounded-field border border-line bg-surface p-1"
        >
          {options.map((m) => (
            <li key={m.user.id}>
              <button
                type="button"
                onClick={() => {
                  onChange(value.replace(/@([^\s@]*)$/, `@${m.user.display_name} `));
                  onMention(m.user.display_name, m.user.id);
                  field.current?.focus();
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 rounded-lg border-0 bg-transparent px-2 py-1.5 text-left text-sm hover:bg-surface-header",
                  focusRing,
                )}
              >
                <Avatar name={m.user.display_name} tone={toneFor(m.user.id)} size={22} />
                {m.user.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
      <textarea
        id={id}
        ref={field}
        rows={1}
        value={value}
        maxLength={2000}
        placeholder="Add a caption"
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            onSubmit();
          }
        }}
        className="min-h-[42px] resize-none rounded-field border border-line bg-surface px-3 py-2.5 font-sans text-[15px] leading-snug text-ink outline-none placeholder:text-ink-meta focus:border-blue"
      />
    </div>
  );
}

export type TrayProps = {
  items: TrayItem[] | null;
  recipient: string;
  original: boolean;
  preparer: Preparer;
  members: Member[];
  onItems: (items: TrayItem[]) => void;
  onOriginal: (original: boolean) => void;
  onMention: (name: string, userId: string) => void;
  onAddMore: () => void;
  onClose: () => void;
  onSend: () => void;
};

export function SendTray({
  items,
  recipient,
  original,
  preparer,
  members,
  onItems,
  onOriginal,
  onMention,
  onAddMore,
  onClose,
  onSend,
}: TrayProps) {
  const list = items ?? [];
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [said, setSaid] = useState("");
  const tiles = useRef(new Map<string, HTMLButtonElement>());
  const drag = useRef<{ id: string; x: number; y: number; moved: boolean } | null>(null);
  const [sizes, setSizes] = useState<Record<string, number>>({});

  const selected = list.find((i) => i.id === selectedId) ?? list[0];
  const selectedIndex = selected ? list.indexOf(selected) : -1;

  // The total counts what will be sent: a photo's prepared size once known, for the chosen quality.
  useEffect(() => {
    let live = true;
    for (const item of list) {
      void preparer.get(item, original).then((p) => {
        if (live)
          setSizes((s) =>
            s[`${item.id}:${original}`] === p.file.size
              ? s
              : { ...s, [`${item.id}:${original}`]: p.file.size },
          );
      });
    }
    return () => {
      live = false;
    };
  }, [list, preparer, original]);
  const total = list.reduce((n, i) => n + (sizes[`${i.id}:${original}`] ?? i.file.size), 0);
  const large = list.filter((i) => i.file.size > WARN_MEDIA_BYTES);

  const update = useCallback((next: TrayItem[]) => onItems(next), [onItems]);

  const move = (from: number, to: number, announce = true) => {
    if (to < 0 || to >= list.length || from === to) return;
    const next = moveItem(list, from, to);
    update(next);
    if (announce)
      setSaid(`${itemLabel(list[from] as TrayItem, from)} moved to position ${to + 1} of ${list.length}.`);
  };

  const remove = (item: TrayItem) => {
    const index = list.indexOf(item);
    const next = list.filter((i) => i !== item);
    preparer.forget(item.id);
    setSaid(`${itemLabel(item, index)} removed.`);
    update(next);
    const neighbour = next[Math.min(index, next.length - 1)];
    if (neighbour) {
      setSelectedId(neighbour.id);
      requestAnimationFrame(() => tiles.current.get(neighbour.id)?.focus());
    }
  };

  const onKey = (item: TrayItem, index: number) => (e: KeyboardEvent<HTMLButtonElement>) => {
    const step = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -4, ArrowDown: 4 }[e.key];
    if (e.key === "Delete" || e.key === "Backspace") {
      e.preventDefault();
      remove(item);
      return;
    }
    if (step === undefined) return;
    e.preventDefault();
    const to = Math.max(0, Math.min(list.length - 1, index + step));
    if (e.altKey) {
      move(index, to);
      requestAnimationFrame(() => tiles.current.get(item.id)?.focus());
    } else {
      const target = list[to];
      if (target) tiles.current.get(target.id)?.focus();
    }
  };

  const onPointerDown = (item: TrayItem) => (e: PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    drag.current = { id: item.id, x: e.clientX, y: e.clientY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent<HTMLUListElement>) => {
    const d = drag.current;
    if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.x, e.clientY - d.y) < DRAG_PX) return;
    if (!d.moved) {
      d.moved = true;
      setDragId(d.id);
    }
    const over = document.elementFromPoint?.(e.clientX, e.clientY)?.closest("[data-tray-index]");
    const to = over ? Number(over.getAttribute("data-tray-index")) : -1;
    const from = list.findIndex((i) => i.id === d.id);
    if (to >= 0 && from >= 0 && to !== from) move(from, to, false);
  };

  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    setDragId(null);
    if (d?.moved) {
      const at = list.findIndex((i) => i.id === d.id);
      const item = list[at];
      if (item) setSaid(`${itemLabel(item, at)} moved to position ${at + 1} of ${list.length}.`);
    }
  };

  // A click that ends a drag is not a tap on the tile.
  const select = (item: TrayItem) => {
    if (dragId) return;
    setSelectedId(item.id);
  };

  const setCaption = (value: string) => {
    if (!selected) return;
    update(list.map((i) => (i === selected ? { ...i, caption: value } : i)));
  };

  const open = items !== null && list.length > 0;
  return (
    <Sheet open={open} onClose={onClose} fixed={false} title={`Send to ${recipient}`} className="px-5 pb-6">
      <ul
        aria-label="Items to send"
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        className="m-0 mt-4 grid list-none grid-cols-4 gap-2 p-0"
      >
        {list.map((item, i) => (
          <Tile
            key={item.id}
            item={item}
            index={i}
            count={list.length}
            selected={item === selected}
            dragging={item.id === dragId}
            preparer={preparer}
            original={original}
            onSelect={() => select(item)}
            onRemove={() => remove(item)}
            onKey={onKey(item, i)}
            onPointerDown={onPointerDown(item)}
            register={(el) => {
              if (el) tiles.current.set(item.id, el);
              else tiles.current.delete(item.id);
            }}
          />
        ))}
      </ul>
      <p aria-live="polite" className="sr-only">
        {said}
      </p>

      {large.length > 0 && (
        <p
          role="alert"
          className="m-0 mt-3 rounded-field bg-surface-header px-3.5 py-2.5 text-[13px] leading-snug text-ink"
        >
          {large.length === 1
            ? `${large[0]?.file.name || "This file"} is ${formatBytes(large[0]?.file.size ?? 0)}.`
            : `${large.length} of these files are over 100 MB.`}{" "}
          Large files take a while to upload and can use a lot of mobile data.
        </p>
      )}

      {selected && (
        <CaptionField
          key={selected.id}
          label={`Caption for ${itemLabel(selected, selectedIndex).replace(/^(Photo|Video|File)/, (w) => w.toLowerCase())}`}
          value={selected.caption}
          members={members}
          onChange={setCaption}
          onMention={onMention}
          onSubmit={onSend}
        />
      )}

      <div className="mt-3 flex items-center justify-between gap-3 text-[13px]">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            role="switch"
            aria-checked={original}
            checked={original}
            onChange={(e) => onOriginal(e.target.checked)}
            className="h-4 w-4 accent-blue"
          />
          Send original quality
        </label>
        <span className="text-ink-secondary">
          {countLabel(list)} · {formatBytes(total)}
        </span>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Button variant="outline" onClick={onAddMore}>
          Add more
        </Button>
        <Button onClick={onSend} disabled={!list.length}>
          {`Send ${list.length}`}
        </Button>
      </div>
    </Sheet>
  );
}
